
import { useState, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { formatPrice } from "@/lib/utils";

interface PriceRangeFilterProps {
  min: number;
  max: number;
  step?: number;
  initialMin: number;
  initialMax: number;
  onPriceChange: (min: number, max: number) => void;
}

const PriceRangeFilter = ({
  min = 0,
  max = 50000000,
  step = 100000,
  initialMin,
  initialMax,
  onPriceChange,
}: PriceRangeFilterProps) => {
  const [priceRange, setPriceRange] = useState<[number, number]>([
    initialMin,
    initialMax,
  ]);

  const [inputValues, setInputValues] = useState({
    min: initialMin.toString(),
    max: initialMax.toString(),
  });

  useEffect(() => {
    setPriceRange([initialMin, initialMax]);
    setInputValues({
      min: initialMin.toString(),
      max: initialMax.toString(),
    });
  }, [initialMin, initialMax]);

  const handleSliderChange = (values: number[]) => {
    const [newMin, newMax] = values as [number, number];
    setPriceRange([newMin, newMax]);
    setInputValues({
      min: newMin.toString(),
      max: newMax.toString(),
    });
    onPriceChange(newMin, newMax);
  };

  const handleInputChange = (value: string, type: 'min' | 'max') => {
    let numValue = parseInt(value) || 0;
    numValue = Math.max(min, Math.min(max, numValue));

    const newInputValues = {
      ...inputValues,
      [type]: value,
    };
    setInputValues(newInputValues);

    if (!isNaN(numValue)) {
      const newRange = [...priceRange] as [number, number];
      if (type === 'min') {
        newRange[0] = Math.min(numValue, priceRange[1]);
      } else {
        newRange[1] = Math.max(numValue, priceRange[0]);
      }
      setPriceRange(newRange);
      onPriceChange(newRange[0], newRange[1]);
    }
  };

  return (
    <div className="p-4 border-b border-gray-200">
      <h3 className="font-medium text-sm text-gray-500 mb-3">PRICE RANGE</h3>
      <div className="px-2">
        <Slider
          defaultValue={priceRange}
          min={min}
          max={max}
          step={step}
          value={priceRange}
          onValueChange={handleSliderChange}
          className="my-6"
        />
        
        <div className="flex justify-between items-center gap-4 mt-4">
          <div className="flex-1">
            <Input
              type="number"
              value={inputValues.min}
              onChange={(e) => handleInputChange(e.target.value, 'min')}
              min={min}
              max={max}
              step={step}
              className="w-full"
              placeholder="Min Price"
            />
          </div>
          <div className="text-sm text-gray-400">to</div>
          <div className="flex-1">
            <Input
              type="number"
              value={inputValues.max}
              onChange={(e) => handleInputChange(e.target.value, 'max')}
              min={min}
              max={max}
              step={step}
              className="w-full"
              placeholder="Max Price"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceRangeFilter;
